import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, X, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { StepIndicator } from "@/components/ui/StepIndicator";
import RegenLoader from "@/components/shared/RegenLoader";
import { BulkIntakeService } from "@/services/bulkIntakeService";
import { JobService } from "@/services/jobService";
import { Input } from "@/components/ui/Input";
import useLiveResource from "@/hooks/useLiveResource";
import { buildReviewSchema, SALARY_MAX_DIGITS } from "@/pages/user/confirm-details/validation.schema";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import StepPersonal from "@/pages/user/confirm-details/components/steps/StepPersonal";
import { ALTERNATE_EMAIL_COPY } from "@/pages/user/confirm-details/components/steps/alternateEmailCopy";
import StepSkills from "@/pages/user/confirm-details/components/steps/StepSkills";
import StepExperience from "@/pages/user/confirm-details/components/steps/StepExperience";
import StepEducation from "@/pages/user/confirm-details/components/steps/StepEducation";
import StepSalary from "@/pages/user/confirm-details/components/steps/StepSalary";
import StepMoreInfo from "@/pages/user/confirm-details/components/steps/StepMoreInfo";
import ResumePreview from "../candidate-analysis/edit-profile/components/ResumePreview";
import { Card } from "@/components/ui/Card";
import { loadDefaultCurrency, getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import ResumeValidationModal from "@/components/shared/ResumeValidationModal";
import { SplitReviewLayout, ReviewFormPane, ReviewStatusChip, TOOLBAR_BUTTON } from "@/components/shared/SplitReviewLayout";
import { buildSaveStatus, getFirstErrorPath } from "@/utils/reviewFormStatus";
import { droppedFileReason, droppedSummary } from "./droppedFiles";
import { normalizeAlternateEmail } from "@/utils/alternateEmail";
import { mapDraftToFormValues, mapFormValuesToDraft } from "./bulkReviewMappers";
import { requiredLanguageNames } from "@/utils/languageRequirements";
import FailedItemPanel from "./FailedItemPanel";
import AlertModal from "@/components/shared/AlertModal";
import { Badge } from "@/components/ui/Badge";

const SECTIONS = [
  { key: "personal", label: "Personal Info" },
  { key: "skills", label: "Skills & Language" },
  { key: "experience", label: "Experience" },
  { key: "education", label: "Education" },
  { key: "salary", label: "Salary & Availability" },
  { key: "moreinfo", label: "More Info" },
];

const BulkExtractionReview = () => {
  const { batchId, jobId } = useParams();
  const navigate = useNavigate();
  const [resumeFile, setResumeFile] = useState(null);
  const setHeader = useOutletContext();
  const [items, setItems] = useState([]);
  const [activeItemId, setActiveItemId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingItemId, setSavingItemId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const sectionRefs = useRef({});
  const [highlightedSection, setHighlightedSection] = useState(null);
  // Every non-discarded item must be REVIEWED before Confirm All goes live —
  // EXTRACTING, VALIDATION_WARNING, FAILED and MANUAL_PENDING all block it,
  // because each is waiting on the recruiter (or the machine) rather than done.
  const reviewableItems = items.filter((it) => it.status === "REVIEWED");
  const unresolvedItems = items.filter((it) => it.status !== "REVIEWED");
  // An all-discarded batch also has nothing unresolved, so this alone doesn't
  // gate Confirm All — see `batchEmpty` below, which does, with its own tooltip.
  const allReviewed = unresolvedItems.length === 0;
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmEmailEditing, setConfirmEmailEditing] = useState(false);
  // Only ever held while the address is being edited — `/auth/email`
  // verifies it, and it is cleared the moment the call returns.
  const [confirmPassword, setConfirmPassword] = useState("");
  // The account email as last fetched from the server — lets "Edit" be
  // cancelled back to the saved address instead of leaving in-progress edits.
  const [savedAccountEmail, setSavedAccountEmail] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmErrorCode, setConfirmErrorCode] = useState(null);
  const [confirmSentTo, setConfirmSentTo] = useState(null);
  // Server-driven cap on retries per item — never hard-code 3, a tenant can
  // lower it.
  const [maxRetries, setMaxRetries] = useState(3);
  // Per-item async state for the FAILED decision panel's three actions.
  const [retryingItemId, setRetryingItemId] = useState(null);
  const [manualLoadingItemId, setManualLoadingItemId] = useState(null);
  const [abortingItemId, setAbortingItemId] = useState(null);
  const [itemErrors, setItemErrors] = useState({});
  // Set to open the "remove this resume?" confirm — never removes without asking.
  const [abortConfirmItem, setAbortConfirmItem] = useState(null);
  // Set to open the "retry will lose what you typed" confirm for a MANUAL_PENDING item.
  const [retryConfirmItemId, setRetryConfirmItemId] = useState(null);
  // Set only when this job has its bulk email step off, so the
  // request-confirmation call finalized the batch itself.
  const [finalResult, setFinalResult] = useState(null);
  // null until the job has been read. Decides whether "Confirm All"
  // opens the email modal at all — with the email step off there is no address to
  // collect, so the modal would only ever be a dead click-through. The response
  // still decides what actually happened; this is UI routing only.
  const [emailConsentRequired, setEmailConsentRequired] = useState(null);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [validationErrorItemIds, setValidationErrorItemIds] = useState(new Set());
  // FIFO queue of {item_id, file_name, validation} — items whose pre-extraction
  // validation flagged them. Only the head is shown; two items finishing close
  // together both still queue instead of one silently overwriting the other.
  const [validationQueue, setValidationQueue] = useState([]);
  const [validationActionLoading, setValidationActionLoading] = useState(false);
  // Total resumes uploaded to this batch (not the current, possibly-shrunk-by-
  // discard, `items` list) — decides Go Back (single-item batch) vs Ignore
  // (multi-item batch), per spec.
  const [batchItemCount, setBatchItemCount] = useState(0);
  // Discarded items are dropped from `items` entirely (so the review flow /
  // stepper never sees them) but the Confirm All dialog still needs them to
  // explain why a resume the recruiter uploaded won't be processed.
  const [discardedItems, setDiscardedItems] = useState([]);

  const FIELD_TO_SECTION = {
    personal_information: "personal",
    skills: "skills",
    experience: "experience",
    education: "education",
    compensation: "salary",
    additionalSections: "moreinfo",
    social: "moreinfo",
  };

  // The batch payload is the authority — abort/manual choices are never
  // broadcast over the socket, and a socket can drop. Used on mount and to
  // resync after any 409 that means local state went stale.
  const loadBatch = useCallback(async () => {
    try {
      const data = await BulkIntakeService.getBatch(batchId);
      const allItems = data.items || [];
      // A reload loses every review tick unless recruiter_reviewed maps back
      // to the local-only REVIEWED status — the server never sends REVIEWED
      // itself. Skip the remap for MANUAL_PENDING: that flag only ever
      // reflects a *previous* completed pass, not the in-progress retype.
      const visibleItems = allItems
        .filter((it) => it.status !== "DISCARDED")
        .map((it) => (it.recruiter_reviewed && it.status !== "MANUAL_PENDING" ? { ...it, status: "REVIEWED" } : it));
      setItems(visibleItems);
      setBatchItemCount(data.item_count ?? visibleItems.length);
      setMaxRetries(data.max_retries ?? 3);
      setDiscardedItems(allItems.filter((it) => it.status === "DISCARDED"));
      setActiveItemId((cur) => {
        if (cur && visibleItems.some((it) => it.item_id === cur)) return cur;
        const firstReady = visibleItems.find((it) => it.status === "EXTRACTED");
        return firstReady?.item_id ?? visibleItems[0]?.item_id ?? null;
      });
      // Re-queue any item still awaiting a validation decision so the popup
      // isn't lost across a reload or a resync, without duplicating one
      // that's already queued.
      const pending = visibleItems.filter((it) => it.status === "VALIDATION_WARNING");
      setValidationQueue((prev) => {
        const existingIds = new Set(prev.map((q) => q.item_id));
        const additions = pending
          .filter((it) => !existingIds.has(it.item_id))
          .map((it) => ({ item_id: it.item_id, file_name: it.file_name, validation: it.validation_result }));
        return additions.length ? [...prev, ...additions] : prev;
      });
    } catch (err) {
      console.error("Failed to load batch:", err);
    }
  }, [batchId]);

  const openConfirmModal = useCallback(async () => {
    setConfirmModalOpen(true);
    setConfirmError(null);
    setConfirmErrorCode(null);
    setConfirmSentTo(null);
    setFinalResult(null);
    // No email step to prep when the tenant has confirmation off — the modal
    // is only there to show what's included/excluded before submitting.
    if (emailConsentRequired === false) return;
    try {
      const { email } = await BulkIntakeService.getAccountEmail();
      setConfirmEmail(email ?? "");
      setSavedAccountEmail(email ?? "");
      setConfirmEmailEditing(!email);
    } catch (err) {
      console.error("Failed to fetch account email:", err);
    }
  }, [emailConsentRequired]);

  const submitBatch = useCallback(
    async (email) => {
      // Keep sending the email even though it's ignored when this job has its
      // bulk email step off — the pre-read flag can be stale if someone edits the
      // job mid-session, and in two-factor mode it's still verified against the
      // account email.
      const res = await BulkIntakeService.requestBatchConfirmation(batchId, email);

      if (res?.email_confirmation_required !== false) {
        setConfirmSentTo(res.email_masked);
      } else {
        // Single-factor: this call WAS the finalize — the candidates already
        // exist and the response carries the counts /confirm/finalize returns.
        setFinalResult({
          confirmed_count: res.confirmed_count ?? 0,
          dropped_count: res.dropped_count ?? 0,
          dropped_files: res.dropped_files ?? [],
        });
      }
    },
    [batchId],
  );

  // Local state can be stale (a socket can drop, another tab can act), so every
  // error branch that means "the batch moved on without us" refetches rather
  // than trusting what's already on screen.
  const applyConfirmError = useCallback(
    (err) => {
      const code = err?.response?.data?.error_code;
      let message;
      if (code === "BULK_INTAKE_NO_READY_ITEMS") {
        message = "Nothing is left in this batch to submit. Start a new upload.";
      } else if (code === "BULK_INTAKE_JOB_CLOSED") {
        message = "This job is no longer accepting applications, so this batch can't be processed.";
      } else if (code === "BULK_INTAKE_ITEM_DATA_INCOMPLETE") {
        // The server holds this line even though the form can save a blank
        // name: a candidate with no name is a row the candidate list cannot
        // render, and one with no email can never be matched by any dedup.
        const incomplete = err?.response?.data?.data?.items || [];
        if (incomplete[0]?.item_id) setActiveItemId(incomplete[0].item_id);
        message = incomplete.length
          ? `These resumes are missing a name or an email address: ${incomplete.map((it) => it.file_name).join(", ")}.`
          : "Some resumes are missing a name or an email address.";
        loadBatch();
      } else if (code === "BULK_INTAKE_UNRESOLVED_ITEMS") {
        const unresolved = err?.response?.data?.data?.items || [];
        if (unresolved[0]?.item_id) setActiveItemId(unresolved[0].item_id);
        message = unresolved.length
          ? `These resumes still need a decision: ${unresolved.map((it) => it.file_name).join(", ")}.`
          : "Some resumes still need a decision before this batch can be submitted.";
        loadBatch();
      } else {
        message = err?.response?.data?.message || "Couldn't submit this batch. Please try again.";
      }
      setConfirmError(message);
      setConfirmErrorCode(code);
      console.error("Failed to submit batch:", err);
    },
    [loadBatch],
  );

  const handleSendConfirmation = useCallback(async () => {
    if (!batchId) return;
    setConfirmLoading(true);
    setConfirmError(null);
    setConfirmErrorCode(null);
    try {
      if (confirmEmailEditing) {
        await BulkIntakeService.updateAccountEmail(confirmEmail, confirmPassword);
        setConfirmPassword("");
        setConfirmEmailEditing(false);
      }
      const { email } = await BulkIntakeService.getAccountEmail();
      // submitBatch IS the confirmation request, and it sets confirmSentTo from
      // the response. Calling requestBatchConfirmation again here minted a second
      // token and sent a second mail, which voided the first link.
      await submitBatch(email);
    } catch (err) {
      applyConfirmError(err);
    } finally {
      setConfirmLoading(false);
    }
  }, [batchId, confirmEmail, confirmEmailEditing, confirmPassword, submitBatch, applyConfirmError]);

  // Entry point for the "Confirm All" button. Mirrors the handler in
  // ../index.jsx but against this screen's local state (batchId from useParams).
  // Always opens the modal first, so the recruiter sees which files are
  // excluded (and why) before anything is submitted — regardless of whether
  // the tenant requires email confirmation.
  const handleConfirmAll = useCallback(() => {
    if (!batchId) return;
    openConfirmModal();
  }, [batchId, openConfirmModal]);

  // Footer action for the modal when this job has its bulk email step
  // off — no address to collect, so this submits the batch directly and the
  // modal switches to the finalResult view (failed/skipped files + reasons).
  const handleConfirmNoEmail = useCallback(async () => {
    if (!batchId) return;
    setConfirmingAll(true);
    setConfirmError(null);
    setConfirmErrorCode(null);
    try {
      const { email } = await BulkIntakeService.getAccountEmail().catch(() => ({
        email: null,
      }));
      await submitBatch(email);
      // Deliberately does NOT navigate away: submitBatch sets `finalResult`, and
      // the modal switches to that view to show which files were duplicates or
      // errored and were skipped. Navigating here would make that the recruiter's
      // only report of what actually happened, unreachable.
    } catch (err) {
      applyConfirmError(err);
    } finally {
      setConfirmingAll(false);
    }
  }, [batchId, submitBatch, applyConfirmError]);

  const schema = useMemo(() => buildReviewSchema(mandatoryFields, false, { languagesShape: "rows" }), [mandatoryFields]);
  const methods = useForm({ resolver: zodResolver(schema), mode: "onChange" });
  const watchedValues = useWatch({ control: methods.control });

  const patchItem = useCallback((itemId, patch) => {
    setItems((prev) => prev.map((it) => (it.item_id === itemId ? { ...it, ...patch } : it)));
  }, []);

  const currentValidationItem = validationQueue[0] ?? null;
  const isSingleItemBatch = batchItemCount === 1;
  // Set when the recruiter's Ignore / Go Back / Continue Anyway call failed
  // server-side. Cleared whenever a different item reaches the head of the
  // queue, so a stale message from a previous item never carries over.
  const [validationActionError, setValidationActionError] = useState(null);

  useEffect(() => {
    setValidationActionError(null);
  }, [currentValidationItem?.item_id]);

  const dequeueValidationItem = useCallback((itemId) => {
    setValidationQueue((prev) => prev.filter((q) => q.item_id !== itemId));
  }, []);

  // A failed state-changing call (BULK_INTAKE_INVALID_STATE /
  // BULK_INTAKE_ITEM_NOT_FOUND) means the batch already moved on without us
  // in another tab or request — retrying the same action can only fail
  // again, so this resyncs from the server instead. loadBatch() re-queues
  // the item if it's genuinely still VALIDATION_WARNING there, or drops it
  // if it isn't. Any other failure (a network blip, a rate limit) leaves the
  // popup open with the message on screen, so the recruiter can just retry.
  const isStaleStateError = (err) => {
    const code = err?.response?.data?.error_code;
    return code === "BULK_INTAKE_INVALID_STATE" || code === "BULK_INTAKE_ITEM_NOT_FOUND";
  };

  const handleValidationIgnore = useCallback(
    async (itemId) => {
      setValidationActionLoading(true);
      setValidationActionError(null);
      try {
        await BulkIntakeService.discardItem(itemId);
        setItems((prev) => prev.filter((it) => it.item_id !== itemId));
        // If the discarded item was the one being reviewed, jump to whatever is
        // next in the batch instead of leaving the review panel on an empty
        // (loader-only) state.
        setActiveItemId((cur) => {
          if (cur !== itemId) return cur;
          const next = items.find((it) => it.item_id !== itemId);
          return next?.item_id ?? null;
        });
        dequeueValidationItem(itemId);
      } catch (err) {
        console.error(`Failed to ignore item ${itemId}:`, err);
        if (isStaleStateError(err)) {
          dequeueValidationItem(itemId);
          loadBatch();
        } else {
          setValidationActionError(err?.response?.data?.message || "Couldn't ignore this resume. Please try again.");
        }
      } finally {
        setValidationActionLoading(false);
      }
    },
    [items, dequeueValidationItem, loadBatch],
  );

  const handleValidationGoBack = useCallback(
    async (itemId) => {
      setValidationActionLoading(true);
      setValidationActionError(null);
      try {
        await BulkIntakeService.discardItem(itemId);
        dequeueValidationItem(itemId);
        navigate(`/admin/job-detail/${jobId}/bulk-upload`);
      } catch (err) {
        console.error(`Failed to discard item ${itemId} on Go Back:`, err);
        if (isStaleStateError(err)) {
          dequeueValidationItem(itemId);
          loadBatch();
        } else {
          setValidationActionError(err?.response?.data?.message || "Couldn't remove this resume. Please try again.");
        }
      } finally {
        setValidationActionLoading(false);
      }
    },
    [dequeueValidationItem, loadBatch, navigate, jobId],
  );

  const handleValidationContinueAnyway = useCallback(
    async (itemId) => {
      setValidationActionLoading(true);
      setValidationActionError(null);
      try {
        await BulkIntakeService.continueItemExtraction(itemId);
        patchItem(itemId, { status: "EXTRACTING" });
        dequeueValidationItem(itemId);
      } catch (err) {
        console.error(`Failed to continue item ${itemId} after validation warning:`, err);
        if (isStaleStateError(err)) {
          dequeueValidationItem(itemId);
          loadBatch();
        } else {
          setValidationActionError(err?.response?.data?.message || "Couldn't continue with this resume. Please try again.");
        }
      } finally {
        setValidationActionLoading(false);
      }
    },
    [dequeueValidationItem, loadBatch, patchItem],
  );


  const scrollToSection = useCallback((key) => {
    const el = sectionRefs.current[key];
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedSection(key);

    setTimeout(() => {
      setHighlightedSection((current) => (current === key ? null : current));
    }, 2000);
  }, []);

  const [currencySign, setCurrencySign] = useState(getDefaultCurrencyFallback().currencySign);
  // The job's own language rows — prefilled first and locked in the editor,
  // same as the candidate-facing apply flow (see `languagesSlot` below).
  const [jobRequiredLanguages, setJobRequiredLanguages] = useState([]);

  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const [res, jobRes] = await Promise.all([JobService.fetchJobFieldConfig(jobId), JobService.fetchJobById(jobId).catch(() => null), loadDefaultCurrency()]);

        if (res.success && res.data) {
          const all = [...(res.data.always_mandatory || []).map((f) => f.key), ...(res.data.configured_mandatory || []).map((f) => f.key)];
          setMandatoryFields(all);
        }

        if (jobRes?.data && jobRes.data.currency_sign !== undefined) {
          setCurrencySign(jobRes.data.currency_sign || getDefaultCurrencyFallback().currencySign);
        }
        setJobRequiredLanguages(jobRes?.data?.languages_required || []);

        // The consent flag is the job's, not the tenant's. Reading the org-wide
        // value here is what told a recruiter a confirmation link was coming for
        // a job whose email step was off: the modal appeared, offered to send a
        // link, and the batch then submitted immediately with no email — the
        // server was reading the job while this screen read the tenant.
        //
        // Fail-safe unchanged: anything other than an explicit `false` means the
        // email step is on, because a wasted click beats stranding a batch that
        // really does need an address. That covers the job read failing, which
        // the `.catch(() => null)` above swallows.
        setEmailConsentRequired(jobRes?.data?.require_bulk_email_consent !== false);
      } catch (err) {
        console.error("Failed to fetch field config or job details:", err);
        setEmailConsentRequired(true);
      }
    })();
  }, [jobId]);

  const sectionCardClass = (key) => `transition-all duration-500 rounded-xl ${highlightedSection === key ? "ring-2 ring-tone-blue/50" : ""}`;

  useEffect(() => {
    setHeader({
      title: "Review and Confirm",
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    loadBatch().finally(() => setLoading(false));
  }, [loadBatch]);

  // Every batch event is one read of the batch. `loadBatch` already does the
  // whole job the five per-item handlers were doing between them: the items and
  // their statuses, the validation queue, the discarded list, the retry
  // counters, and selecting the first resume that became ready.
  useLiveResource({
    keys: `bulk_batch:${batchId}`,
    enabled: Boolean(batchId),
    onChange: loadBatch,
  });

  const activeItem = items.find((it) => it.item_id === activeItemId);
  // What the save bar says about the resume on screen — same rule on every
  // review screen, so it lives in reviewFormStatus.
  // salaryFieldsRequired matches the flag passed to buildReviewSchema above, so
  // the chip counts exactly what the resolver refuses.
  const saveStatus = useMemo(
    () => buildSaveStatus(watchedValues, mandatoryFields, methods.formState.errors, { salaryFieldsRequired: false }),
    [watchedValues, mandatoryFields, methods.formState.errors],
  );

  useEffect(() => {
    setSaveError(null);
    if (activeItem?.status === "EXTRACTED" || activeItem?.status === "REVIEWED") {
      // reviewed_data is local-only — a reloaded REVIEWED item never has it,
      // so fall back to the draft rather than resetting the form to nothing.
      const source = activeItem.reviewed_data ?? mapDraftToFormValues(activeItem.draft_data, activeItem, jobRequiredLanguages);
      methods.reset(source);

      // Validate immediately after load so the step indicator can go red
      // on first render, without waiting for a save attempt.
      methods.trigger().then((valid) => {
        setValidationErrorItemIds((prev) => {
          const next = new Set(prev);
          if (valid) {
            next.delete(activeItem.item_id);
          } else {
            next.add(activeItem.item_id);
          }
          return next;
        });
      });
    } else if (activeItem?.status === "MANUAL_PENDING") {
      // Always starts blank — this is a fresh typed entry, not a resume to
      // preload from.
      methods.reset(mapDraftToFormValues(null, activeItem, jobRequiredLanguages));
      methods.trigger();
    }
  }, [activeItem?.item_id, activeItem?.status, activeItem?.draft_data, activeItem?.reviewed_data, jobRequiredLanguages, methods]);

  const extractFileName = (contentDisposition, contentType = "") => {
    if (!contentDisposition) {
      if (contentType.includes("pdf")) return "resume.pdf";
      if (contentType.includes("word") || contentType.includes("officedocument")) return "resume.docx";
      return "resume";
    }
    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
    const asciiMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    if (asciiMatch?.[1]) return asciiMatch[1];
    if (contentType.includes("pdf")) return "resume.pdf";
    if (contentType.includes("word") || contentType.includes("officedocument")) return "resume.docx";
    return "resume";
  };

  // Puts a server-side field error onto the form. `fields` are the draft names
  // the server sent, `paths` maps each to its form path. Returns the first path
  // it marked, for the caller to scroll to.
  const markServerFieldErrors = useCallback(
    (fields, paths, message) => {
      let firstPath = null;
      fields.forEach((field) => {
        const path = paths[field];
        if (!path) return;
        firstPath = firstPath ?? path;
        methods.setError(path, { type: "manual", message });
      });
      return firstPath;
    },
    [methods],
  );

  // Scrolls the form to a field and focuses it, falling back to its section
  // when the field isn't rendered (a collapsed or absent optional block).
  const focusFieldPath = useCallback(
    (path, fallbackSectionKey) => {
      if (!path) return;
      const el = document.querySelector(`[data-name="${path}"]`) ?? document.querySelector(`[name="${path}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();
      } else if (fallbackSectionKey) {
        scrollToSection(fallbackSectionKey);
      }
    },
    [scrollToSection],
  );

  const handleSaveReview = useCallback(async () => {
    if (!activeItemId) return;

    const valid = await methods.trigger();

    if (!valid) {
      setValidationErrorItemIds((prev) => new Set(prev).add(activeItemId));

      const firstPath = getFirstErrorPath(methods.formState.errors);
      focusFieldPath(firstPath, FIELD_TO_SECTION[firstPath?.split(".")[0]]);
      return;
    }

    setValidationErrorItemIds((prev) => {
      const next = new Set(prev);
      next.delete(activeItemId);
      return next;
    });

    setSavingItemId(activeItemId);
    setSaveError(null);
    const formData = methods.getValues();
    const draftData = mapFormValuesToDraft(formData, activeItem?.draft_data);
    const alternateEmail = normalizeAlternateEmail(formData.personal_information?.alternate_email);

    try {
      await BulkIntakeService.saveItemEdits(activeItemId, draftData, alternateEmail);
      patchItem(activeItemId, { status: "REVIEWED", reviewed_data: formData, draft_data: draftData, alternate_email: alternateEmail });

      // Jump to the next resume still awaiting review, so the recruiter can
      // move through the whole batch without hunting for the next step.
      // If everything is reviewed, stay put — confirming is only ever done
      // from the "Confirm All" button pinned at the top of this page.
      const currentIndex = items.findIndex((it) => it.item_id === activeItemId);
      const isPending = (it) => it.item_id !== activeItemId && it.status === "EXTRACTED";
      const nextPending = items.find((it, idx) => idx > currentIndex && isPending(it)) ?? items.find(isPending);
      if (nextPending) setActiveItemId(nextPending.item_id);
    } catch (err) {
      const code = err?.response?.data?.error_code;
      if (code === "BULK_INTAKE_MANUAL_DATA_INCOMPLETE") {
        const missingFieldPaths = {
          full_name: "personal_information.name",
          email: "personal_information.email",
          phone: "personal_information.phone",
        };
        const missingFieldLabels = { full_name: "Name", email: "Email", phone: "Phone" };
        const missing = err?.response?.data?.data?.missing_fields || [];
        const firstPath = markServerFieldErrors(missing, missingFieldPaths, "This field is required");
        focusFieldPath(firstPath, "personal");
        // Name the fields the server actually refused, so the recruiter reads
        // what is missing rather than the full list of three every time.
        const labels = missing.map((f) => missingFieldLabels[f]).filter(Boolean);
        setSaveError(
          labels.length
            ? `${labels.join(", ")} ${labels.length === 1 ? "is" : "are"} required before this resume can be saved.`
            : "Name, email and phone are required before this resume can be saved.",
        );
      } else if (code === "BULK_INTAKE_DRAFT_DATA_INVALID") {
        // The server refused a value its own column can't hold — the two salary
        // fields and total experience. Mark the field rather than showing a
        // message the recruiter can't act on. Keyed by the draft name the server
        // sends, which is not always the form's name for it.
        const invalidFieldPaths = {
          current_salary: "compensation.current_salary",
          expected_salary: "compensation.expected_salary",
          total_experience_years: "compensation.total_experience",
        };
        const invalidFieldLabels = {
          current_salary: "Gross salary",
          expected_salary: "Expected gross salary",
          total_experience_years: "Total experience",
        };
        const invalidFieldRules = {
          current_salary: `numbers only, up to ${SALARY_MAX_DIGITS} digits`,
          expected_salary: `numbers only, up to ${SALARY_MAX_DIGITS} digits`,
          total_experience_years: "a number of years up to 100",
        };
        const invalid = (err?.response?.data?.data?.invalid_fields || []).filter((f) => invalidFieldPaths[f]);
        let firstPath = null;
        invalid.forEach((field) => {
          const marked = markServerFieldErrors([field], invalidFieldPaths, `Enter ${invalidFieldRules[field]}`);
          firstPath = firstPath ?? marked;
        });
        focusFieldPath(firstPath, "salary");
        const labels = invalid.map((f) => `${invalidFieldLabels[f]} must be ${invalidFieldRules[f]}`);
        setSaveError(
          labels.length
            ? `${labels.join(". ")}. Leave a field empty if you don't know it.`
            : err?.response?.data?.message || "Some values can't be saved. Check the highlighted fields.",
        );
      } else {
        setSaveError(err?.response?.data?.message || "Couldn't save this resume. Please try again.");
      }
    } finally {
      setSavingItemId(null);
    }
  }, [activeItemId, activeItem?.draft_data, items, methods, patchItem, focusFieldPath, markServerFieldErrors]);

  const { isValid } = methods.formState;

  useEffect(() => {
    if (isValid && activeItemId && validationErrorItemIds.has(activeItemId)) {
      setValidationErrorItemIds((prev) => {
        const next = new Set(prev);
        next.delete(activeItemId);
        return next;
      });
    }
  }, [isValid, activeItemId, validationErrorItemIds]);

  const isReviewReady = activeItem?.status === "EXTRACTED" || activeItem?.status === "REVIEWED" || activeItem?.status === "MANUAL_PENDING";

  // Remembers which item the panel is currently showing so an in-flight
  // resume-file fetch for a previously-viewed item can't overwrite the
  // preview with stale data (out-of-order responses when clicking through
  // items quickly showed the wrong resume, or none at all).
  const activeItemIdRef = useRef(null);

  const fetchItemResume = useCallback(async (itemId) => {
    if (!itemId) return;
    try {
      const res = await BulkIntakeService.fetchItemResumeFile(itemId); // add this method to bulkIntakeService
      if (activeItemIdRef.current !== itemId) {
        return;
      }
      const contentType = res?.headers?.["content-type"] || "";
      const contentDisposition = res?.headers?.["content-disposition"] || "";
      const filename = extractFileName(contentDisposition, contentType);
      const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: contentType });
      const objectUrl = URL.createObjectURL(blob);

      setResumeFile((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return { filename, objectUrl, blob, type: contentType };
      });
    } catch (err) {
      if (activeItemIdRef.current !== itemId) {
        return;
      }
      console.error("Failed to load resume preview for item:", itemId, err);
    }
  }, []);

  const handleDownloadResume = useCallback(() => {
    if (!resumeFile?.objectUrl) return;
    const a = document.createElement("a");
    a.href = resumeFile.objectUrl;
    a.download = resumeFile.filename || "resume";
    a.click();
  }, [resumeFile]);

  // re-fetch whenever the active step changes
  useEffect(() => {
    activeItemIdRef.current = activeItemId;
    if (activeItem?.status === "EXTRACTED" || activeItem?.status === "REVIEWED" || activeItem?.status === "MANUAL_PENDING" || activeItem?.status === "FAILED") {
      fetchItemResume(activeItemId);
    } else {
      setResumeFile((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return null;
      });
    }

    return () => {
      setResumeFile((prev) => {
        if (prev?.objectUrl) URL.revokeObjectURL(prev.objectUrl);
        return null;
      });
    };
  }, [activeItemId, activeItem?.status, fetchItemResume]);

  const setItemError = useCallback((itemId, message) => {
    setItemErrors((prev) => ({ ...prev, [itemId]: message }));
  }, []);

  const performRetry = useCallback(
    async (itemId) => {
      setRetryingItemId(itemId);
      setItemError(itemId, null);
      try {
        const res = await BulkIntakeService.retryItemExtraction(itemId);
        patchItem(itemId, {
          status: "EXTRACTING",
          retry_count: res.retry_count,
          retries_remaining: res.retries_remaining,
          // request_item_retry writes entry_mode back to "ai" — a retried manual
          // item is an AI item again. bulk_intake_item_ready carries no
          // entry_mode, so without this the "Typed by recruiter" badge would
          // survive a successful retry until the next reload.
          entry_mode: "ai",
        });
      } catch (err) {
        const code = err?.response?.data?.error_code;
        if (code === "BULK_INTAKE_RETRY_LIMIT_REACHED") {
          setItemError(itemId, "No retries left for this resume. Fill it in manually, or remove it from the batch.");
          loadBatch();
        } else if (code === "BULK_INTAKE_INVALID_STATE" || code === "BULK_INTAKE_ITEM_NOT_FOUND") {
          loadBatch();
        } else if (code === "MODULE_TEMPORARILY_UNAVAILABLE") {
          setItemError(itemId, "Extraction is temporarily unavailable. Try again later, or fill this resume in manually.");
        } else if (code === "RATE_LIMIT_EXCEEDED") {
          setItemError(itemId, "Too many requests. Wait a moment and try again.");
        } else {
          setItemError(itemId, err?.response?.data?.message || "Couldn't retry this resume. Please try again.");
        }
        console.error(`Failed to retry item ${itemId}:`, err);
      } finally {
        setRetryingItemId(null);
        setRetryConfirmItemId(null);
      }
    },
    [patchItem, loadBatch, setItemError],
  );

  // A MANUAL_PENDING item may already have typed data in the form — confirm
  // before throwing it away for a fresh extraction attempt.
  const handleRetryRequest = useCallback(
    (item) => {
      if (item.status === "MANUAL_PENDING") {
        setRetryConfirmItemId(item.item_id);
        return;
      }
      performRetry(item.item_id);
    },
    [performRetry],
  );

  const handleStartManualEntry = useCallback(
    async (itemId) => {
      setManualLoadingItemId(itemId);
      setItemError(itemId, null);
      try {
        await BulkIntakeService.startManualEntry(itemId);
        patchItem(itemId, { status: "MANUAL_PENDING", entry_mode: "manual" });
        setActiveItemId(itemId);
      } catch (err) {
        const code = err?.response?.data?.error_code;
        if (code === "BULK_INTAKE_INVALID_STATE" || code === "BULK_INTAKE_ITEM_NOT_FOUND") {
          loadBatch();
        } else {
          setItemError(itemId, err?.response?.data?.message || "Couldn't switch to manual entry. Please try again.");
        }
        console.error(`Failed to start manual entry for item ${itemId}:`, err);
      } finally {
        setManualLoadingItemId(null);
      }
    },
    [patchItem, loadBatch, setItemError],
  );

  const handleAbortConfirmed = useCallback(async () => {
    const itemId = abortConfirmItem?.item_id;
    if (!itemId) return;
    setAbortingItemId(itemId);
    try {
      await BulkIntakeService.discardItem(itemId);
      setItems((prev) => prev.filter((it) => it.item_id !== itemId));
      setDiscardedItems((prev) => [...prev, { ...abortConfirmItem, status: "DISCARDED" }]);
      setActiveItemId((cur) => {
        if (cur !== itemId) return cur;
        const next = items.find((it) => it.item_id !== itemId);
        return next?.item_id ?? null;
      });
    } catch (err) {
      const code = err?.response?.data?.error_code;
      if (code === "BULK_INTAKE_INVALID_STATE" || code === "BULK_INTAKE_ITEM_NOT_FOUND") {
        loadBatch();
      } else {
        setItemError(itemId, err?.response?.data?.message || "Couldn't remove this resume. Please try again.");
      }
      console.error(`Failed to abort item ${itemId}:`, err);
    } finally {
      setAbortingItemId(null);
      setAbortConfirmItem(null);
    }
  }, [abortConfirmItem, items, loadBatch, setItemError]);

  // Only DISCARDED items are ever excluded — a FAILED item is a blocker that
  // holds up Confirm All, not something quietly left out of the batch.
  const excludedItems = discardedItems.map((it) => ({
    item_id: it.item_id,
    file_name: it.file_name,
    reason: "Removed by you",
  }));

  const reviewedPercent = items.length ? Math.round((reviewableItems.length / items.length) * 100) : 0;

  const stepsConfig = items.map((it, i) => ({ key: it.item_id, label: it.file_name || `Resume ${i + 1}` }));
  const stepsReady = items.map((it) => it.status === "EXTRACTED" || it.status === "REVIEWED" || it.status === "MANUAL_PENDING");
  const stepsCompleted = items.map((it) => it.status === "REVIEWED");
  const stepsWithErrors = items.map((it) => it.status === "FAILED" || validationErrorItemIds.has(it.item_id));
  const stepsWithWarning = items.map((it) => it.status === "VALIDATION_WARNING" || it.status === "MANUAL_PENDING");
  const currentStepIndex = items.findIndex((it) => it.item_id === activeItemId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  // Every uploaded resume ended up discarded (ignored as irrelevant, or
  // aborted individually) — nothing is left to extract or review. Without
  // this, `activeItem` is undefined and the layout falls through to the
  // "still processing" spinner below, which never resolves.
  const batchEmpty = items.length === 0;

  const resumePane = <ResumePreview file={resumeFile} onDownload={handleDownloadResume} />;

  const formPane = (
    <ReviewFormPane
      sections={SECTIONS}
      onSectionClick={scrollToSection}
      headerRight={
        activeItem?.entry_mode === "manual" && (
          <Badge label="Typed by recruiter" variant="solid" colorIndex={7} className="shrink-0 rounded-full text-xs" badgeStyle="text-[10px] py-0.5" />
        )
      }
      footer={
        <>
          {(itemErrors[activeItemId] || saveError) && <p className="mb-1.5 text-[11px] text-tone-rose">{itemErrors[activeItemId] || saveError}</p>}
          {activeItem?.status === "MANUAL_PENDING" && !activeItem.can_retry && activeItem.can_abort && (
            <p className="mb-1.5 text-[11px] text-tone-amber">No retries left for this resume.</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <ReviewStatusChip label={saveStatus.label} tone={saveStatus.tone} />

            <div className="ml-auto flex items-center gap-2">
              {/* Abort follows can_abort alone. Retry and abort are two
                  separate escapes from MANUAL_PENDING — gating this one on
                  "no retries left" left a recruiter who chose "Fill in
                  manually" and then changed their mind with no way out but
                  typing the whole form or burning three paid AI calls. */}
              {activeItem?.status === "MANUAL_PENDING" && activeItem.can_abort && (
                <Button
                  onClick={() => setAbortConfirmItem(activeItem)}
                  disabled={abortingItemId === activeItemId || savingItemId === activeItemId}
                  className={cn(TOOLBAR_BUTTON, "border border-tone-rose text-tone-rose")}
                >
                  {abortingItemId === activeItemId ? "Removing…" : "Abort"}
                </Button>
              )}
              {activeItem?.status === "MANUAL_PENDING" && activeItem.can_retry && (
                <Button
                  onClick={() => handleRetryRequest(activeItem)}
                  disabled={retryingItemId === activeItemId || savingItemId === activeItemId}
                  className={cn(TOOLBAR_BUTTON, "border border-border-primary bg-background-primary text-text-primary")}
                >
                  {retryingItemId === activeItemId ? "Retrying…" : "Retry extraction"}
                </Button>
              )}
              <Button onClick={handleSaveReview} disabled={savingItemId === activeItemId} className={cn(TOOLBAR_BUTTON, "bg-button-secondary text-text-base")}>
                {savingItemId === activeItemId ? "Saving…" : activeItem?.status === "REVIEWED" ? "Save changes" : "Save & mark reviewed"}
              </Button>
            </div>
          </div>
        </>
      }
    >
      {activeItem?.status === "MANUAL_PENDING" && activeItem.extraction_error && (
        <Card className="border-tone-amber/30 bg-tone-amber-soft px-4 py-3">
          <p className="text-[11px] text-tone-amber">Extraction failed for this resume: {activeItem.extraction_error} You're filling it in manually.</p>
        </Card>
      )}
      <FormProvider {...methods}>
        <Card ref={(el) => (sectionRefs.current.personal = el)} className={sectionCardClass("personal")}>
          <StepPersonal mandatoryFields={mandatoryFields} alternateEmailHelperText={ALTERNATE_EMAIL_COPY.bulkReview.helperText} />
        </Card>

        <Card ref={(el) => (sectionRefs.current.skills = el)} className={sectionCardClass("skills")}>
          <StepSkills
            mandatoryFields={mandatoryFields}
            languagesSlot={(disabled, required) => (
              <LanguageProficiencyRows
                name="skills.languages"
                disabled={disabled}
                emptyText="No languages added."
                allowUnknownProficiency
                lockedLanguages={requiredLanguageNames(jobRequiredLanguages)}
                languageRequired={required?.language}
                levelledLanguages={jobRequiredLanguages}
              />
            )}
          />
        </Card>

        <Card ref={(el) => (sectionRefs.current.experience = el)} className={sectionCardClass("experience")}>
          <StepExperience mandatoryFields={mandatoryFields} />
        </Card>

        <Card ref={(el) => (sectionRefs.current.education = el)} className={sectionCardClass("education")}>
          <StepEducation mandatoryFields={mandatoryFields} />
        </Card>

        <Card ref={(el) => (sectionRefs.current.salary = el)} className={sectionCardClass("salary")}>
          <StepSalary mandatoryFields={mandatoryFields} salaryFieldsRequired={false} currencySign={currencySign} />
        </Card>

        <Card ref={(el) => (sectionRefs.current.moreinfo = el)} className={sectionCardClass("moreinfo")}>
          <StepMoreInfo mandatoryFields={mandatoryFields} showSocials={false} />
        </Card>
      </FormProvider>
    </ReviewFormPane>
  );

  const failedPane = activeItem?.status === "FAILED" && (
    <FailedItemPanel
      item={activeItem}
      maxRetries={maxRetries}
      retrying={retryingItemId === activeItem.item_id}
      manualLoading={manualLoadingItemId === activeItem.item_id}
      aborting={abortingItemId === activeItem.item_id}
      error={itemErrors[activeItem.item_id]}
      onRetry={() => handleRetryRequest(activeItem)}
      onManualEntry={() => handleStartManualEntry(activeItem.item_id)}
      onAbortRequest={() => setAbortConfirmItem(activeItem)}
    />
  );

  return (
    <>
      <SplitReviewLayout
        onBack={() => navigate(`/admin/job-detail/${jobId}/bulk-upload`)}
        backLabel="Back to upload"
        actions={
          <>
            {confirmError && !confirmModalOpen && <p className="text-[11px] text-status-error">{confirmError}</p>}

            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap text-xs font-medium text-text-primary">
                {reviewableItems.length} of {items.length} reviewed
              </span>
              <div className="h-1.5 w-20 overflow-hidden rounded-full bg-background-tertiary">
                <div className="h-full rounded-full bg-tone-emerald transition-all duration-300" style={{ width: `${reviewedPercent}%` }} />
              </div>
            </div>

            <Button
              onClick={handleConfirmAll}
              disabled={!allReviewed || batchEmpty || !!finalResult || confirmingAll || emailConsentRequired === null}
              // The rail already colours the resumes still waiting; the tooltip
              // answers "why is this greyed out?" without spending a line on it.
              title={
                batchEmpty
                  ? "No resumes left in this batch"
                  : allReviewed
                    ? undefined
                    : `${unresolvedItems.length} resume${unresolvedItems.length !== 1 ? "s" : ""} still ${unresolvedItems.length !== 1 ? "need" : "needs"} a decision`
              }
              className={cn(TOOLBAR_BUTTON, "bg-button-secondary text-text-base")}
            >
              {confirmingAll ? "Submitting…" : "Confirm All"}
            </Button>
          </>
        }
        belowToolbar={
          <StepIndicator
            stepsConfig={stepsConfig}
            currentStep={currentStepIndex + 1}
            stepClick={(i) => setActiveItemId(items[i]?.item_id)}
            stepsWithErrors={stepsWithErrors}
            stepsWithWarning={stepsWithWarning}
            stepsReady={stepsReady}
            stepsCompleted={stepsCompleted}
            containerClass="mb-0"
          />
        }
        left={
          batchEmpty ? (
            <Card className="flex h-full flex-col items-center justify-center gap-3 text-center px-6">
              <p className="text-text-primary text-sm font-medium">No resumes left to process</p>
              <p className="text-text-secondary text-xs max-w-xs">
                Every resume in this batch was ignored or removed. Go back to upload more resumes for this job.
              </p>
              <Button
                onClick={() => navigate(`/admin/job-detail/${jobId}/bulk-upload`)}
                className={cn(TOOLBAR_BUTTON, "bg-button-secondary text-text-base")}
              >
                Back to upload
              </Button>
            </Card>
          ) : isReviewReady ? (
            formPane
          ) : failedPane ? (
            failedPane
          ) : (
            <Card className="flex h-full flex-col items-center justify-center">
              <RegenLoader />
            </Card>
          )
        }
        right={!batchEmpty && (isReviewReady || failedPane) ? resumePane : null}
      />

      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="border-b-0 pb-0">
            <DialogTitle>{finalResult ? "Batch submitted" : "Confirm all candidates"}</DialogTitle>
            <DialogDescription>
              {finalResult
                ? `${finalResult.confirmed_count} of ${finalResult.confirmed_count + finalResult.dropped_count} candidates were added to this job.${
                    finalResult.dropped_count > 0 ? ` ${droppedSummary(finalResult.dropped_count)}` : ""
                  }`
                : confirmSentTo
                  ? `A confirmation link has been sent to ${confirmSentTo}.`
                  : emailConsentRequired === false
                    ? "Review what will be submitted before creating these candidates."
                    : "We'll email you a link to confirm before any candidates are created."}
            </DialogDescription>
          </DialogHeader>

          {!finalResult && (
            <div className="px-6 pt-3 pb-1 flex flex-col gap-1 max-h-56 overflow-y-auto">
              {reviewableItems.map((it) => (
                <div key={it.item_id} className="flex items-center gap-2 text-[11px] text-text-secondary">
                  <Check size={12} className="shrink-0 text-tone-emerald" />
                  <span className="truncate text-tone-emerald">{it.file_name || "Untitled resume"}</span>
                </div>
              ))}
              {excludedItems.map((it) => (
                <div key={it.item_id} className="flex items-center gap-2 text-[11px] text-text-secondary">
                  <X size={12} className="shrink-0 text-tone-rose" />
                  <span className="truncate text-tone-rose">
                    {it.file_name || "Untitled resume"} — {it.reason}
                  </span>
                </div>
              ))}
            </div>
          )}

          {finalResult?.dropped_files?.length > 0 && (
            <div className="px-6 pt-3 pb-2">
              <ul className="text-[11px] text-text-secondary list-disc pl-4 space-y-0.5 max-h-40 overflow-y-auto">
                {finalResult.dropped_files.map((f, i) => (
                  <li key={i}>
                    {f.file_name} — {droppedFileReason(f)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!confirmSentTo && !finalResult && emailConsentRequired !== false && (
            <div className="px-6 pt-3 pb-2 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={confirmEmail}
                  disabled={!confirmEmailEditing}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 text-[13px] rounded-lg border border-border-primary bg-background-primary px-3 py-2 text-text-primary disabled:opacity-70"
                />
                {!confirmEmailEditing ? (
                  <Button onClick={() => setConfirmEmailEditing(true)} className="text-[11px] bg-button-primary text-text-invert rounded-lg shrink-0">
                    Edit
                  </Button>
                ) : (
                  savedAccountEmail && (
                    <Button
                      onClick={() => {
                        setConfirmEmail(savedAccountEmail);
                        setConfirmPassword("");
                        setConfirmEmailEditing(false);
                      }}
                      title="Cancel editing"
                      className="shrink-0 rounded-lg p-1.5 text-text-secondary hover:bg-background-tertiary"
                    >
                      <X size={14} />
                    </Button>
                  )
                )}
              </div>
              {confirmEmailEditing && (
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Current password"
                  className="text-[13px] rounded-lg border border-border-primary bg-background-primary px-3 py-2 text-text-primary"
                />
              )}
              {confirmError && <p className="text-[11px] text-status-error">{confirmError}</p>}
            </div>
          )}

          {!finalResult && emailConsentRequired === false && confirmError && (
            <div className="px-6 pt-3 pb-2">
              <p className="text-[11px] text-status-error">{confirmError}</p>
            </div>
          )}

          <DialogFooter className="border-t-0 pt-2">
            {finalResult ? (
              <Button
                onClick={() => {
                  setConfirmModalOpen(false);
                  navigate(`/admin/job-detail/${jobId}`);
                }}
                className="text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2"
              >
                View candidates
              </Button>
            ) : confirmErrorCode === "BULK_INTAKE_NO_READY_ITEMS" || confirmErrorCode === "BULK_INTAKE_JOB_CLOSED" ? (
              <Button
                onClick={() => {
                  setConfirmModalOpen(false);
                  navigate(`/admin/job-detail/${jobId}`);
                }}
                className="text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2"
              >
                Back to job
              </Button>
            ) : confirmSentTo ? (
              <Button
                onClick={() => {
                  setConfirmModalOpen(false);
                  navigate("/admin/jobs");
                }}
                className="text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2"
              >
                Back to jobs
              </Button>
            ) : emailConsentRequired === false ? (
              <Button
                onClick={handleConfirmNoEmail}
                disabled={confirmingAll}
                className="text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {confirmingAll ? "Submitting…" : "Confirm All"}
              </Button>
            ) : (
              <Button
                onClick={handleSendConfirmation}
                disabled={confirmLoading || !confirmEmail || (confirmEmailEditing && !confirmPassword)}
                className="text-xs bg-button-secondary text-text-base rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {confirmLoading ? "Sending…" : "Send confirmation link"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResumeValidationModal
        open={!!currentValidationItem}
        fileName={currentValidationItem?.file_name}
        validation={currentValidationItem?.validation}
        mode={isSingleItemBatch ? "individual" : "bulk"}
        loading={validationActionLoading}
        error={validationActionError}
        onGoBack={() => handleValidationGoBack(currentValidationItem.item_id)}
        onIgnore={() => handleValidationIgnore(currentValidationItem.item_id)}
        onContinueAnyway={() => handleValidationContinueAnyway(currentValidationItem.item_id)}
      />

      <AlertModal
        open={!!abortConfirmItem}
        title={`Remove ${abortConfirmItem?.file_name || "this resume"} from this batch?`}
        description="You can't add it back to this batch."
        cancelText="Cancel"
        actionText="Remove"
        loading={abortingItemId === abortConfirmItem?.item_id}
        onOpenChange={(open) => !open && setAbortConfirmItem(null)}
        onCancel={() => setAbortConfirmItem(null)}
        onAction={handleAbortConfirmed}
        actionClassName="bg-tone-rose text-text-base"
      />

      <AlertModal
        open={!!retryConfirmItemId}
        title="Retry extraction?"
        description="Anything you typed here will be lost."
        cancelText="Cancel"
        actionText="Retry"
        loading={retryingItemId === retryConfirmItemId}
        onOpenChange={(open) => !open && setRetryConfirmItemId(null)}
        onCancel={() => setRetryConfirmItemId(null)}
        onAction={() => performRetry(retryConfirmItemId)}
      />
    </>
  );
};

export default BulkExtractionReview;
